import React, { useState } from 'react';
import {
  Box, Paper, Typography, Container, TextField, Button, Alert,
  Divider, Link, CircularProgress, Checkbox, FormControlLabel,
} from '@mui/material';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EmailIcon from '@mui/icons-material/Email';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

const AccountDeletionPage = () => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    reason: '',
    confirmDelete: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      setError('Please fill in all required fields.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      setError('Please enter a valid email address.');
      return;
    }
    if (!form.confirmDelete) {
      setError('Please confirm that you want to delete your account.');
      return;
    }

    setSubmitting(true);

    try {
      // Send deletion request via mailto fallback — this opens the user's email client
      // In production, you could POST this to a backend endpoint that logs the request
      const subject = encodeURIComponent(`Account Deletion Request - ${form.name}`);
      const body = encodeURIComponent(
        `Account Deletion Request\n\n` +
        `Name: ${form.name}\n` +
        `Email: ${form.email}\n` +
        `Phone: ${form.phone}\n` +
        `Reason: ${form.reason || 'Not provided'}\n\n` +
        `I confirm that I want my Gbl Tree account and all associated personal data to be deleted permanently.\n\n` +
        `Please process this request within 30 days as per your Privacy Policy.`
      );
      window.location.href = `mailto:support@gbltree.com?subject=${subject}&body=${body}`;
      setTimeout(() => {
        setSubmitted(true);
        setSubmitting(false);
      }, 600);
    } catch (err) {
      setError('Failed to submit request. Please email support@gbltree.com directly.');
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <Box sx={{ minHeight: '100vh', backgroundColor: '#F8FAFC', py: { xs: 3, md: 6 } }}>
        <Container maxWidth="sm">
          <Paper sx={{ p: { xs: 4, md: 6 }, borderRadius: 3, textAlign: 'center' }}>
            <Box sx={{ width: 72, height: 72, borderRadius: '50%', bgcolor: '#E8F5E9', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 3 }}>
              <CheckCircleIcon sx={{ fontSize: 42, color: '#1E8E3E' }} />
            </Box>
            <Typography variant="h5" fontWeight={700} sx={{ mb: 1.5 }}>
              Request Submitted
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3, lineHeight: 1.7 }}>
              Your account deletion request has been sent. Our team will process it within
              <strong> 30 days</strong> and send you a confirmation email.
            </Typography>
            <Alert severity="info" sx={{ textAlign: 'left', mb: 3, borderRadius: 2 }}>
              If your email client did not open, please email us directly at{' '}
              <Link href="mailto:support@gbltree.com">support@gbltree.com</Link> with your
              registered name, email, and phone number.
            </Alert>
            <Button variant="outlined" onClick={() => setSubmitted(false)} sx={{ textTransform: 'none' }}>
              Submit Another Request
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#F8FAFC', py: { xs: 3, md: 6 } }}>
      <Container maxWidth="sm">
        <Paper sx={{ p: { xs: 3, md: 5 }, borderRadius: 3 }}>
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box sx={{ width: 72, height: 72, borderRadius: '50%', bgcolor: '#FCE8E6', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
              <DeleteForeverIcon sx={{ fontSize: 42, color: '#D93025' }} />
            </Box>
            <Typography variant="h4" fontWeight={800} sx={{ color: '#1E293B', mb: 1 }}>
              Delete Your Account
            </Typography>
            <Typography color="text.secondary" fontSize="0.9rem">
              Gbl Tree Abroad Education
            </Typography>
          </Box>

          {/* Warning */}
          <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 3, borderRadius: 2 }}>
            <Typography fontSize="0.85rem" fontWeight={600} sx={{ mb: 0.5 }}>
              This action cannot be undone
            </Typography>
            <Typography fontSize="0.8rem">
              All your personal information, uploaded documents, and loan application data
              will be permanently deleted. Some data may be retained for regulatory compliance
              as described in our Privacy Policy.
            </Typography>
          </Alert>

          {/* What gets deleted */}
          <Box sx={{ mb: 3, p: 2.5, bgcolor: '#F8FAFC', borderRadius: 2, border: '1px solid #E2E8F0' }}>
            <Typography fontWeight={600} fontSize="0.9rem" sx={{ mb: 1 }}>
              What will be deleted:
            </Typography>
            <Box component="ul" sx={{ pl: 2.5, mb: 0, '& li': { fontSize: '0.85rem', color: '#475569', mb: 0.5 } }}>
              <li>Your profile (name, email, phone, date of birth)</li>
              <li>Course &amp; loan application details</li>
              <li>All uploaded documents (KYC, academic memos, bank statements, etc.)</li>
              <li>Chat messages with advisors</li>
              <li>Firebase authentication data</li>
            </Box>
            <Typography fontWeight={600} fontSize="0.9rem" sx={{ mt: 2, mb: 1 }}>
              What may be retained:
            </Typography>
            <Box component="ul" sx={{ pl: 2.5, mb: 0, '& li': { fontSize: '0.85rem', color: '#475569', mb: 0.5 } }}>
              <li>Loan application records required by banking regulations</li>
              <li>Anonymized data used for fraud prevention</li>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {/* Form */}
          <Box component="form" onSubmit={handleSubmit}>
            <Typography fontWeight={600} fontSize="0.95rem" sx={{ mb: 2 }}>
              Request Account Deletion
            </Typography>

            <TextField
              fullWidth
              required
              label="Full Name"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              sx={{ mb: 2 }}
              size="small"
            />
            <TextField
              fullWidth
              required
              label="Registered Email"
              type="email"
              value={form.email}
              onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
              sx={{ mb: 2 }}
              size="small"
              helperText="The email you used to sign in to the app"
            />
            <TextField
              fullWidth
              required
              label="Phone Number"
              value={form.phone}
              onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
              sx={{ mb: 2 }}
              size="small"
            />
            <TextField
              fullWidth
              label="Reason for Deletion (Optional)"
              value={form.reason}
              onChange={(e) => setForm((p) => ({ ...p, reason: e.target.value }))}
              sx={{ mb: 2 }}
              size="small"
              multiline
              rows={3}
              placeholder="Help us improve by letting us know why you're leaving"
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={form.confirmDelete}
                  onChange={(e) => setForm((p) => ({ ...p, confirmDelete: e.target.checked }))}
                  sx={{ color: '#D93025', '&.Mui-checked': { color: '#D93025' } }}
                />
              }
              label={
                <Typography fontSize="0.85rem" color="text.secondary">
                  I understand this action is permanent and cannot be reversed. I want to
                  delete my Gbl Tree account and all associated data.
                </Typography>
              }
              sx={{ mb: 2, alignItems: 'flex-start' }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <DeleteForeverIcon />}
              sx={{
                bgcolor: '#D93025',
                '&:hover': { bgcolor: '#B3261E' },
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                py: 1.25,
              }}
            >
              {submitting ? 'Submitting...' : 'Request Account Deletion'}
            </Button>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Alternative Method */}
          <Box>
            <Typography fontWeight={600} fontSize="0.9rem" sx={{ mb: 1 }}>
              Already signed in to the app?
            </Typography>
            <Typography fontSize="0.85rem" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.6 }}>
              You can delete your account directly from the app:
              <strong> Profile → Delete Account</strong>
            </Typography>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Contact */}
          <Box sx={{ textAlign: 'center' }}>
            <Typography fontSize="0.85rem" color="text.secondary" sx={{ mb: 1.5 }}>
              Need help? Email us directly:
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
              <EmailIcon sx={{ fontSize: 20, color: '#1A73E8' }} />
              <Link href="mailto:support@gbltree.com" sx={{ fontWeight: 600 }}>
                support@gbltree.com
              </Link>
            </Box>
          </Box>

          <Typography variant="caption" color="text.disabled" align="center" sx={{ display: 'block', mt: 4 }}>
            See our{' '}
            <Link href="/privacy-policy">Privacy Policy</Link>
            {' '}for details on data retention.
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
};

export default AccountDeletionPage;
